// src/managers/ChatFeedManager/ChatFeedManager.jsx
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useConversation } from '@/managers/ConvoManager';
import { generateId } from '@/managers/ConvoManager/ConvoHelpers';
import { authManager } from '@/managers/AuthManager';
import { platformCannon } from './PlatformCannon';
import { getPreflightInjectables } from './PreflightInjectables';
import { botManager } from '@/managers/BotManager';
import { workspaceManager } from '@/managers/workspaceManager';
import { timestampService } from '@/utils/timestampService';
import {
  DEFAULT_PROMPTED_BOT_ID,
  DEFAULT_PROMPTED_BOT_NAME,
  DEFAULT_PROMPTED_SERVER_BOT_ID,
  getBotRuntimeSystemMessage,
} from '@/utils/defaultBotProfile';
import { applyToolPresentationOverrides, isMaintenanceTool, isWorkspaceMemoryTool } from '@/utils/toolPresentation';
import {
  getEffectiveActiveServerIds,
  getEffectiveActiveToolIds,
  getEffectiveToolIterationLimit,
  getWorkspaceForConversation,
} from '@/utils/workspaceAccessSettings';
import { usePanelManager, CONTENT_TYPES } from '@/managers/PanelManager';
import { updateContentBlocksIncremental, finalizeContentBlocks } from '@/managers/ConvoManager/streamParser';
import { StreamEventTypes } from '@/managers/ConvoManager/UniversalResponseSchema';
import { toast } from 'sonner';

const ChatFeedContext = createContext();

const streamProcessor = {
  streamStateCache: new Map(),
  chunkQueue: [],
  updateScheduled: false,
  animationFrameId: null,
  componentHookData: null,

  processQueue: function() {
    if (this.chunkQueue.length === 0) {
      this.updateScheduled = false;
      return;
    }

    const item = this.chunkQueue.shift();
    if (!item) {
      this.updateScheduled = false;
      return;
    }

    if (!this.componentHookData) {
        this.chunkQueue.unshift(item);
        this.updateScheduled = false;
        return;
    }

    if (item.chunk.type === 'WORKSPACE_REFRESH_REQUIRED') {
        window.dispatchEvent(new CustomEvent('workspace-refresh-required'));
        if (this.chunkQueue.length > 0) {
            this.animationFrameId = requestAnimationFrame(() => this.processQueue());
        } else {
            this.updateScheduled = false;
        }
        return;
    }
    
    const { chunk, streamId, conversationId } = item;
    const { updateOptimisticAssistantMessage, messages } = this.componentHookData;
    
    if (chunk.type !== 'content_delta') {
    }
    performance.mark(`trace-${streamId}-cfm-process-start`);


    const optimisticMsg = (messages[conversationId] || []).find(m => m.id === streamId);
    if (!optimisticMsg) {
      if (this.chunkQueue.length > 0) this.animationFrameId = requestAnimationFrame(() => this.processQueue());
      else this.updateScheduled = false;
      return;
    }
    
    if (optimisticMsg._isFinalized) {
      this.streamStateCache.delete(streamId);
      if (this.chunkQueue.length > 0) this.animationFrameId = requestAnimationFrame(() => this.processQueue());
      else this.updateScheduled = false;
      return; 
    }

    if (!this.streamStateCache.has(streamId)) {
        this.streamStateCache.set(streamId, {
            streamEvents: [...(optimisticMsg.streamEvents || [])],
            contentBlocks: [...(optimisticMsg.contentBlocks || [])],
            toolCallsMap: { ...(optimisticMsg.toolCallsMap || {}) },
            thoughtSummary: optimisticMsg.thoughtSummary,
            retrievedMemories: [...(optimisticMsg.retrievedMemories || [])],
            memoryStatus: optimisticMsg.memoryStatus,
            toolStatus: optimisticMsg.toolStatus,
            _memoryBuffer: [...(optimisticMsg._memoryBuffer || [])],
            _hasThinkingStarted: !!optimisticMsg._hasThinkingStarted,
        });
    }
    const liveStreamState = this.streamStateCache.get(streamId);

    liveStreamState.streamEvents.push(chunk);

    if (chunk.type === StreamEventTypes.BLOCK_CREATE && chunk.payload?.type === 'thinking') {
      liveStreamState._hasThinkingStarted = true;
    }

    if (chunk.type === StreamEventTypes.MEMORY_CONTEXT_DELTA && chunk.payload) {
        liveStreamState._memoryBuffer.push({
            content: chunk.payload.contentChunk.trim(),
            memoryId: chunk.payload.memoryId,
        });
    }

    if (liveStreamState._hasThinkingStarted && liveStreamState._memoryBuffer.length > 0) {
        liveStreamState.retrievedMemories.push(...liveStreamState._memoryBuffer);
        liveStreamState._memoryBuffer = [];
    }

    liveStreamState.contentBlocks = updateContentBlocksIncremental(
      liveStreamState.contentBlocks,
      chunk,
      liveStreamState.toolCallsMap
    );
    
    liveStreamState.thoughtSummary = liveStreamState.contentBlocks
      .filter(b => b.type === 'thinking')
      .map(b => b.content)
      .join('\n');
    
    let metadataUpdate = {};
    let statusUpdate = {};

    switch (chunk.type) {
      case StreamEventTypes.IDS:
        statusUpdate.status = 'streaming';
        metadataUpdate = { ...(liveStreamState.metadata || {}), ...chunk.payload };
        break;
      case StreamEventTypes.MEMORY_CONTEXT_START:
        if (!liveStreamState.memoryStatus || liveStreamState.memoryStatus === 'loading') liveStreamState.memoryStatus = 'streaming';
        break;
      case StreamEventTypes.MEMORY_CONTEXT_END:
         if (liveStreamState.memoryStatus === 'streaming') liveStreamState.memoryStatus = 'surfaced';
        break;
      case StreamEventTypes.TOOL_PROCESSING_START:
        liveStreamState.toolStatus = 'processing';
        break;
      case StreamEventTypes.TOOL_PROCESSING_RESULT:
        liveStreamState.toolStatus = 'idle';
        break;
      case StreamEventTypes.STREAM_ERROR:
        statusUpdate.status = 'error';
        statusUpdate.errorDetails = chunk.payload;
        break;
      case StreamEventTypes.STREAM_END:
        statusUpdate.status = 'delivered';
        statusUpdate.isOptimistic = false;
        statusUpdate.toolStatus = 'idle';

        liveStreamState.contentBlocks = finalizeContentBlocks(liveStreamState.contentBlocks);
        if (liveStreamState.memoryStatus === 'streaming') liveStreamState.memoryStatus = 'surfaced';
        else if (liveStreamState.memoryStatus === 'loading') liveStreamState.memoryStatus = 'idle';
        
        if (liveStreamState._memoryBuffer.length > 0) {
            liveStreamState.retrievedMemories.push(...liveStreamState._memoryBuffer);
            liveStreamState._memoryBuffer = [];
        }

        // If any tools were called, the workspace files may have changed — refresh the explorer.
        if (Object.keys(liveStreamState.toolCallsMap).length > 0) {
            window.dispatchEvent(new CustomEvent('workspace-refresh-required'));
        }

        this.streamStateCache.delete(streamId);
        break;
    }

    const finalUpdatePayload = {
        ...liveStreamState,
        ...statusUpdate,
        metadata: { ...(optimisticMsg.metadata || {}), ...metadataUpdate },
    };
    
    updateOptimisticAssistantMessage(conversationId, streamId, finalUpdatePayload);
    
    performance.mark(`trace-${streamId}-cfm-process-end`);
    performance.measure(`CFM Process Time for ${streamId}`, `trace-${streamId}-cfm-process-start`, `trace-${streamId}-cfm-process-end`);

    if (this.chunkQueue.length > 0) {
      this.animationFrameId = requestAnimationFrame(() => this.processQueue());
    } else {
      this.updateScheduled = false;
    }
  },
};

export const useChatFeed = () => {
  const context = useContext(ChatFeedContext);
  if (!context) {
    throw new Error('useChatFeed must be used within a ChatFeedProvider');
  }
  return context;
};

const useAuthSettings = () => {
  const [authState, setAuthState] = useState(() => authManager.getState());

  useEffect(() => {
    const unsubscribe = authManager.subscribe((state) => {
      setAuthState(state);
    });
    return unsubscribe;
  }, []);

  const settings = authState.settings;
  const mcpServers = authState.mcpServers;
  const globalTools = authState.globalTools;
  const isLoaded = authState.settingsLoaded && authState.globalToolsLoaded;

  return {
    useStreaming: settings.zPreferences?.useStreaming ?? true,
    mcpServers,
    globalTools,
    isLoading: !isLoaded,
    usePersonalMemory: settings.zPreferences?.userMeta?.usePersonalMemory ?? false,
    personalMemoryKey: null,
    useDevSettings: settings.zPreferences?.useDevSettings ?? false,
  };
};

export const ChatFeedManager = ({ children }) => {
  const { user } = authManager.getState();
  const conversationHookData = useConversation();
  const panelManager = usePanelManager();

  const {
    useStreaming: settingsUseStreaming,
    isLoading: settingsLoading,
    usePersonalMemory,
    useDevSettings, // Added useDevSettings here for access in cancelStream logic
  } = useAuthSettings();

  const currentAssistantMessageIdRef = useRef(null);
  const [localError, setLocalError] = useState(null);

  const conversationHookDataRef = useRef(conversationHookData);
  useEffect(() => {
    conversationHookDataRef.current = conversationHookData;
  }, [conversationHookData]);
  
  const panelManagerRef = useRef(panelManager);
  useEffect(() => {
    panelManagerRef.current = panelManager;
  }, [panelManager]);

  useEffect(() => {
    streamProcessor.componentHookData = conversationHookData;

    if (streamProcessor.chunkQueue.length > 0 && !streamProcessor.updateScheduled) {
        streamProcessor.updateScheduled = true;
        streamProcessor.animationFrameId = requestAnimationFrame(() => streamProcessor.processQueue());
    }

    return () => {
      streamProcessor.componentHookData = null;
      if (streamProcessor.animationFrameId) {
          cancelAnimationFrame(streamProcessor.animationFrameId);
          streamProcessor.animationFrameId = null;
          streamProcessor.updateScheduled = false;
      }
    };
  }, [conversationHookData]);


  const handleError = (message, code = 'CFM_ERROR') => {
    setLocalError({ message, code });
    toast.error(message);
  };
  
  const addUserAndAssistantMessages = async (userMessageData, assistantMessageId, conversationIdForTurn) => {
    const { 
      sendUserMessage: currentSendUserMessage, 
      addOptimisticAssistantMessage: currentAddOptimisticAssistantMessage,
    } = conversationHookDataRef.current;

    const userMessagePayload = { ...userMessageData, role: 'user' };
    const userMessageTimestamp = userMessageData.timestamp;
    
    if (!(userMessageTimestamp instanceof Date)) {
        return { success: false, error: 'Invalid timestamp provided' };
    }

    await currentAddOptimisticAssistantMessage(conversationIdForTurn, assistantMessageId, {
      status: 'inflight',
      role: 'assistant',
      id: assistantMessageId,
      timestamp: new Date(userMessageTimestamp.getTime() + 1),
      isOptimistic: true,
      toolStatus: 'idle',
      usage: null,
      errorDetails: null,
      transientErrors: [],
      thoughtSummary: null,
      retrievedMemories: [],
      memoryStatus: usePersonalMemory ? 'loading' : 'idle',
      metadata: null,
      transcriptionStatus: null,
      _memoryBuffer: [],
      _hasThinkingStarted: false,
    }); 
    
    const result = await currentSendUserMessage(userMessagePayload);
    return result;
  };

 const buildServerOptions = useCallback((activeConversationMeta, otherOptionsFromInputArea) => {
    const serverOpts = { ...otherOptionsFromInputArea };
    let botVariationUsed;
    if (serverOpts.botVariation || serverOpts.botVariationUsed) {
        botVariationUsed = serverOpts.botVariation || serverOpts.botVariationUsed;
    } else if (activeConversationMeta?.lastUsedMessageSettings?.botVariationUsed) {
        botVariationUsed = activeConversationMeta.lastUsedMessageSettings.botVariationUsed;
    } else {
        botVariationUsed = {
            botId: activeConversationMeta.botId,
            botName: activeConversationMeta.botName,
            modelId: activeConversationMeta.modelId,
            platform: activeConversationMeta.botPlatform,
            model: activeConversationMeta.botModel,
        };
    }
    const normalizedBotVariationUsed = {
      ...botVariationUsed,
      model: typeof botVariationUsed.model === 'string' ? botVariationUsed.model.trim() : botVariationUsed.model,
    };
    const botDetails = normalizedBotVariationUsed.botId ? botManager.getBotById(normalizedBotVariationUsed.botId) : null;
    const isVanillaBot = normalizedBotVariationUsed.botId === 'vanillabot' || botDetails?.id === 'vanillabot';
    const serverBotId = isVanillaBot
      ? 'vanillabot'
      : (botDetails?.serverBotId || normalizedBotVariationUsed.serverBotId || normalizedBotVariationUsed.botId || DEFAULT_PROMPTED_SERVER_BOT_ID);

    const runtimeSystemMessage = botDetails
      ? getBotRuntimeSystemMessage(botDetails)
      : normalizedBotVariationUsed.customSystemMessage;

    serverOpts.botVariationUsed = normalizedBotVariationUsed;
    serverOpts.model = normalizedBotVariationUsed.model;

    serverOpts.botVariationUsed = {
      ...normalizedBotVariationUsed,
      botName: normalizedBotVariationUsed.botName || botDetails?.name || DEFAULT_PROMPTED_BOT_NAME,
      serverBotId,
    };

    if (!isVanillaBot && runtimeSystemMessage) {
      serverOpts.customSystemMessage = runtimeSystemMessage;
    }
    delete serverOpts.botVariation;
    
    const allGlobalTools = authManager.getGlobalTools();
    const allMcpServers = authManager.getMcpServers();
    const workspaceState = workspaceManager.getState();
    const scopedWorkspace = getWorkspaceForConversation(workspaceState, activeConversationMeta);
    const activeToolIds = getEffectiveActiveToolIds({
      conversation: activeConversationMeta,
      workspace: scopedWorkspace,
      fallbackActiveToolIds: [],
    });
    const activeMcpServerIds = getEffectiveActiveServerIds({
      conversation: activeConversationMeta,
      workspace: scopedWorkspace,
      fallbackActiveServerIds: [],
    });
    
    const selectedTools = [];
    const workspaceMemoryToolNameSet = new Set(
      allGlobalTools
        .map((tool) => applyToolPresentationOverrides(tool))
        .filter((tool) => isWorkspaceMemoryTool(tool))
        .map((tool) => tool.nameToPass)
    );
    const workspaceDataEnabled = workspaceMemoryToolNameSet.size > 0
      ? activeToolIds.some((toolId) => workspaceMemoryToolNameSet.has(toolId))
      : false;

    if (allGlobalTools.length > 0 && activeToolIds.length > 0) {
      const activeToolIdsSet = new Set(activeToolIds);
      selectedTools.push(
        ...allGlobalTools
          .map((tool) => applyToolPresentationOverrides(tool))
          .filter((tool) => (
            activeToolIdsSet.has(tool.nameToPass)
            && !tool.maintenanceMode
            && !isMaintenanceTool(tool)
          ))
          .map((tool) => tool.nameToPass),
      );
    }
    if (allMcpServers.length > 0 && activeMcpServerIds.length > 0) {
        const activeMcpServerIdsSet = new Set(activeMcpServerIds);
        selectedTools.push(...allMcpServers.filter(server => activeMcpServerIdsSet.has(server.id)).map(server => server.callableToolId || server.id));
    }
    serverOpts.selectedTools = selectedTools;
    
    const botIdForPrefs = normalizedBotVariationUsed.botId;
    const modelIdForPrefs = normalizedBotVariationUsed.modelId;
    
    const modelInfo = botManager.getState().allPlatformModels.find(m => m.id === modelIdForPrefs);
    if (modelInfo?.hasTools === false) {
      serverOpts.selectedTools = [];
    }

    const hasThinkingCapability = modelInfo?.hasThinking || modelInfo?.platform?.toUpperCase() === 'CLAUDE';

    const userThinkingPrefs = authManager.getThinkingPreferences(botIdForPrefs, modelIdForPrefs);

    let finalThinkingSettings;
    if (userThinkingPrefs !== null) {
        finalThinkingSettings = userThinkingPrefs;
    } else {
        finalThinkingSettings = {
            enabled: hasThinkingCapability,
            budget: 1800,
        };
    }

    serverOpts.allowThinking = finalThinkingSettings.enabled;
    serverOpts.thinkingBudgetTokens = finalThinkingSettings.budget;
    serverOpts.thinkingSettings = finalThinkingSettings;
    
    let modelMaxTokens;
    if (normalizedBotVariationUsed && typeof normalizedBotVariationUsed.maxTokens === 'number' && normalizedBotVariationUsed.maxTokens > 0) {
      modelMaxTokens = normalizedBotVariationUsed.maxTokens;
    } else if (modelInfo?.maxTokens) {
        modelMaxTokens = modelInfo.maxTokens;
    } else if (activeConversationMeta && typeof activeConversationMeta.modelMaxTokens === 'number' && activeConversationMeta.modelMaxTokens > 0) {
        modelMaxTokens = activeConversationMeta.modelMaxTokens;
    }

    if (typeof modelMaxTokens === 'number' && modelMaxTokens > 0) {
        serverOpts.maxOutputTokens = modelMaxTokens;
    } else {
        delete serverOpts.maxOutputTokens;
    }

    serverOpts.temperature = otherOptionsFromInputArea.temperature ?? activeConversationMeta?.temperature ?? 0.7;

    serverOpts.toolIterationLimit = getEffectiveToolIterationLimit({
      conversation: activeConversationMeta,
      workspace: scopedWorkspace,
    });

    serverOpts.incognitoMode = activeConversationMeta?.privacyMode === 'incognito' || !workspaceDataEnabled;

    return serverOpts;
}, []);

  const sendMessageInternal = useCallback(async (messageData, options, isStreaming, filesToPassToCannon = []) => {
    const hook = conversationHookDataRef.current;
    if (!user) return { success: false, error: 'No user authenticated' };
    if (!hook.currentConversationId) return { success: false, error: 'No active conversation selected.'};
    if (hook.isNavigating) return { success: false, error: 'Navigation in progress, please wait.' };

    const conversationIdForTurn = hook.currentConversationId;
    const userMessageId = options.messageId;
    const assistantMessageId = options.assistantMessageId;
    
    let activeConversationMeta = hook.conversations[conversationIdForTurn];
    if (!activeConversationMeta) return { success: false, error: 'Conversation metadata is missing.' };

    if (activeConversationMeta.persisted && !activeConversationMeta.messagesLoaded && !activeConversationMeta.isLoadingMessages) {
        try {
            await hook.loadMoreMessagesIfNeeded(conversationIdForTurn, 20);
            activeConversationMeta = conversationHookDataRef.current.conversations[conversationIdForTurn]; 
            if (!activeConversationMeta || !activeConversationMeta.messagesLoaded) {
                 hook.updateOptimisticAssistantMessage(conversationIdForTurn, assistantMessageId, {
                     status: 'error', errorDetails: { message: 'Failed to load conversation history for sending.', code: 'LOAD_HISTORY_FAILED_PREFLIGHT' }
                 });
                 return { success: false, error: 'Conversation metadata missing or messages failed to load.' };
            }
        } catch (loadError) {
            hook.updateOptimisticAssistantMessage(conversationIdForTurn, assistantMessageId, {
                status: 'error', errorDetails: { message: 'Failed to load conversation history for sending.', code: 'LOAD_HISTORY_FAILED' }
            });
            return { success: false, error: 'Failed to load conversation history.' };
        }
    } else if (activeConversationMeta.persisted && activeConversationMeta.isLoadingMessages) {
        hook.updateOptimisticAssistantMessage(conversationIdForTurn, assistantMessageId, {
            status: 'error', errorDetails: { message: 'Messages are currently loading, please try again shortly.', code: 'MESSAGES_LOADING_CONFLICT' }
        });
        return { success: false, error: 'Messages are currently loading.' };
    }

    const { rawContent, contentBlocks, files = {}, currentMediaItems = [], mediaItems = [], images = [], role, timestamp: userMessageTimestamp, hasAudio } = messageData;
    currentAssistantMessageIdRef.current = assistantMessageId;

    const normalizedMediaItems = [
      ...(Array.isArray(currentMediaItems) ? currentMediaItems : []),
      ...(Array.isArray(mediaItems) ? mediaItems : []),
      ...(Array.isArray(images) ? images : []),
    ].filter(Boolean);

    const hasMediaInFilesMap = Object.values(files || {}).some((file) => {
      if (!file) return false;
      const mimeType = file.type || file.mimeType || '';
      return mimeType.startsWith('image/') || Boolean(file.storageUrl || file.url);
    });
    
    const hasTextContent = rawContent && rawContent.replace('[SYSTEM_AWAITING_TRANSCRIPT]', '').trim().length > 0;
    
    if (!hasTextContent && normalizedMediaItems.length === 0 && !hasMediaInFilesMap && filesToPassToCannon.length === 0) {
      hook.updateOptimisticAssistantMessage(conversationIdForTurn, assistantMessageId, {
        status: 'error', errorDetails: { message: "Cannot send an empty message.", code: "EMPTY_MESSAGE_ERROR" }
      });
      return { success: false, error: "Cannot send an empty message." };
    }
    
    const serverBoundMediaItems = normalizedMediaItems;
    const serverOptions = buildServerOptions(activeConversationMeta, options);

    try {
      const userMessagePayloadForSend = {
        id: userMessageId,
        rawContent,
        contentBlocks,
        files,
        images: normalizedMediaItems,
        mediaItems: normalizedMediaItems,
        currentMediaItems: normalizedMediaItems,
        role,
        timestamp: userMessageTimestamp,
        hasAudio
      };
      const sendResult = await addUserAndAssistantMessages(userMessagePayloadForSend, assistantMessageId, conversationIdForTurn);

      if (!sendResult.success) {
        hook.updateOptimisticAssistantMessage(conversationIdForTurn, assistantMessageId, {
            status: 'error', errorDetails: { message: sendResult.error || 'Failed to persist user message.', code: 'PERSISTENCE_USER_MSG_FAILED' }
        });
        return { success: false, error: sendResult.error || 'Failed to send user message' };
      }

      activeConversationMeta = conversationHookDataRef.current.conversations[conversationIdForTurn];
      const fullCurrentConversationForPreflight = {
        ...activeConversationMeta,
        messages: conversationHookDataRef.current.messages[conversationIdForTurn] || []
      };

      if (!activeConversationMeta || !fullCurrentConversationForPreflight.messages) {
        const errorMsg = 'Conversation context lost after user message persistence.';
        hook.updateOptimisticAssistantMessage(conversationIdForTurn, assistantMessageId, {
            status: 'error', errorDetails: { message: errorMsg, code: 'CONTEXT_LOST_POST_PERSIST' }
        });
        return { success: false, error: errorMsg };
      }

      const newUserMessageDataForPreflight = {
        id: userMessageId, role: 'user', rawContent: rawContent, files: Object.values(files || {}), images: normalizedMediaItems,
        mediaItems: normalizedMediaItems,
        currentMediaItems: normalizedMediaItems, timestamp: userMessageTimestamp
      };
      
      const platformForPreflight = serverOptions.botVariationUsed?.platform || activeConversationMeta?.botPlatform || 'OPENAI';
      const { processedConversation, appendToSystemMessage } = await getPreflightInjectables(
          fullCurrentConversationForPreflight, 
          newUserMessageDataForPreflight, 
          platformForPreflight,
          null
      );

      const serverReadyMessages = processedConversation.map(msg => {
          const messageForServer = {
            id: msg.id,
            role: msg.role,
            content: msg.processedLlmContent || msg.content || (msg.role === 'user' ? '' : null),
          };

          if (msg.tool_calls && msg.tool_calls.length > 0) {
              messageForServer.tool_calls = msg.tool_calls;
          }
          if (msg.role === 'tool' && msg.tool_call_id) {
              messageForServer.tool_call_id = msg.tool_call_id;
          }

          if (msg.id === userMessageId && serverBoundMediaItems.length > 0) {
            messageForServer.mediaItems = serverBoundMediaItems;
          }

          return messageForServer;
      });

      const botId = serverOptions.botVariationUsed?.serverBotId || activeConversationMeta?.serverBotId || DEFAULT_PROMPTED_SERVER_BOT_ID;
      
      const payloadForServer = {
        userId: user.uid,
        platform: platformForPreflight,
        model: serverOptions.model,
        conversationId: conversationIdForTurn,
        userMessageId: userMessageId,
        assistantMessageId: assistantMessageId,
        messages: serverReadyMessages,
        botId: botId,
        botName: serverOptions.botVariationUsed?.botName || activeConversationMeta?.botName || DEFAULT_PROMPTED_BOT_NAME,
        customSystemMessage: serverOptions.customSystemMessage,
        appendToSystemMessage: appendToSystemMessage,
        temperature: serverOptions.temperature,
        maxOutputTokens: serverOptions.maxOutputTokens,
        options: {
          streamToClient: isStreaming,
          usePersonalMemory: usePersonalMemory,
          useDevTools: useDevSettings,
          selectedTools: serverOptions.selectedTools,
          allowThinking: serverOptions.allowThinking,
          thinkingBudgetTokens: serverOptions.thinkingBudgetTokens,
          workspaceContextId: activeConversationMeta?.workspaceContextId,
          hasConfig: !!botId,
          hasThinkingConfig: serverOptions.allowThinking,
          directToolCall: options.directToolCall,
          toolIterationLimit: serverOptions.toolIterationLimit,
          incognitoMode: serverOptions.incognitoMode,
        },
      };

      Object.keys(payloadForServer).forEach(key => (payloadForServer[key] === undefined) && delete payloadForServer[key]);
      if (payloadForServer.options) {
        Object.keys(payloadForServer.options).forEach(key => (payloadForServer.options[key] === undefined) && delete payloadForServer.options[key]);
        if (Object.keys(payloadForServer.options).length === 0) delete payloadForServer.options;
      }
      
      const cannonClientOptions = { stream: isStreaming, onChunk: null, streamId: assistantMessageId };

      if (isStreaming) {
        cannonClientOptions.onChunk = (chunkData) => {
          const { chunk, streamId } = chunkData;
          if (!chunk || !chunk.type || streamId !== assistantMessageId) return;
          performance.mark(`trace-${streamId}-chunk-received-by-cfm`);
          streamProcessor.chunkQueue.push({ chunk, streamId, conversationId: conversationIdForTurn });
          if (!streamProcessor.updateScheduled) {
            streamProcessor.updateScheduled = true;
            streamProcessor.animationFrameId = requestAnimationFrame(() => streamProcessor.processQueue());
          }
        };
      }

      const result = await platformCannon.sendRequest(platformForPreflight, payloadForServer, filesToPassToCannon.length > 0 ? filesToPassToCannon : undefined, cannonClientOptions);
      
      if (result.cancelled) {
          return { success: false, cancelled: true, error: result.error };
      }

      if (result.success !== false) {
        const finalHookState = conversationHookDataRef.current;
        const finalOptimisticMsg = (finalHookState.messages[conversationIdForTurn] || []).find(m => m.id === assistantMessageId);
        
        if (finalOptimisticMsg) {
            finalHookState.updateOptimisticAssistantMessage(conversationIdForTurn, assistantMessageId, {
                status: 'delivered',
                isOptimistic: false,
                toolStatus: 'idle',
                messageSettings: serverOptions,
                usage: result.usage || finalOptimisticMsg.usage,
                errorDetails: null,
            });
        }
      } else {
        const cannonError = result.error || new Error('Unknown error from PlatformCannon');
        const errorStatus = isStreaming ? 'error_streaming' : 'error_receiving_response';
        const finalHookState = conversationHookDataRef.current;
        finalHookState.updateOptimisticAssistantMessage(conversationIdForTurn, assistantMessageId, {
            status: errorStatus, errorDetails: { message: cannonError?.message || 'An error occurred.', code: cannonError?.code || 'CANNON_ERROR' }
        });
      }

      return {
        success: result.success,
        cancelled: result.cancelled || false,
        error: result.error,
        userMessageId: result.userMessageId || userMessageId,
        assistantMessageId: result.assistantMessageId || assistantMessageId,
        content: result.fullContent,
      };

    } catch (error) {
      const caughtError = error;
      if (caughtError.name === 'AbortError') {
          return { success: false, error: caughtError, cancelled: true };
      }
      
      const errorStatus = isStreaming ? 'error_streaming_system' : 'error_sending_system';
      const finalHookStateOnError = conversationHookDataRef.current;
      if (assistantMessageId) {
          finalHookStateOnError.updateOptimisticAssistantMessage(conversationIdForTurn, assistantMessageId, {
              status: errorStatus, errorDetails: { message: caughtError?.message || 'An unexpected system error occurred.', code: caughtError?.code || 'UNEXPECTED_SYSTEM_ERROR' }
          });
      }
      return { success: false, error: caughtError, cancelled: false };
    }
  }, [user, buildServerOptions, usePersonalMemory, useDevSettings]);

  const sendMessage = useCallback((messageData, options, filesToPassToCannon = []) => {
    return sendMessageInternal(messageData, options, false, filesToPassToCannon);
  }, [sendMessageInternal]);

  const sendStreamingMessage = useCallback((messageData, options, filesToPassToCannon = []) => {
    return sendMessageInternal(messageData, options, true, filesToPassToCannon);
  }, [sendMessageInternal]);

  const cancelStream = useCallback(async (assistantIdToCancel) => {
    const assistantIdToAbort = assistantIdToCancel || currentAssistantMessageIdRef.current;
    const hook = conversationHookDataRef.current;
    const { user } = authManager.getState();
    const { currentConversationId, updateOptimisticAssistantMessage, messages } = hook;

    if (!currentConversationId || !assistantIdToAbort || !user) {
      return false;
    }

    platformCannon.abortStream(assistantIdToAbort, user.uid);
    platformCannon.cancelRequest(assistantIdToAbort);

    const msgToCancel = (messages[currentConversationId] || []).find(m => m.id === assistantIdToAbort);

    // Finalize any existing content that was streamed
    const finalizedBlocks = finalizeContentBlocks(msgToCancel?.contentBlocks || []);
    
    // Create a new text block with the cancellation notice
    const cancellationBlock = { type: 'text', content: '\n\n*Message cancelled by user.*' };
    const newContentBlocks = [...finalizedBlocks, cancellationBlock];
    
    // Update the assistant message to a final "delivered" state with the appended notice.
    // This is not an error, but a successful user-initiated action.
    updateOptimisticAssistantMessage(currentConversationId, assistantIdToAbort, { 
      status: 'delivered', 
      isOptimistic: false, 
      toolStatus: 'idle',
      contentBlocks: newContentBlocks,
    });

    currentAssistantMessageIdRef.current = null;
    streamProcessor.streamStateCache.delete(assistantIdToAbort);
    streamProcessor.chunkQueue = streamProcessor.chunkQueue.filter(
        item => item.streamId !== assistantIdToAbort
    );

    return true; // Indicate that cancellation attempt was processed
  }, []);

  const fixMessageFormatting = useCallback(async (assistantMessageId) => {
    const { currentConversationId, updateOptimisticAssistantMessage } = conversationHookDataRef.current;
    const { user } = authManager.getState();

    if (!currentConversationId || !assistantMessageId || !user?.uid) {
      handleError('Cannot fix formatting without required context.', 'FIX_FORMAT_NO_CONTEXT');
      return;
    }
    
    updateOptimisticAssistantMessage(currentConversationId, assistantMessageId, { status: 'reformatting' });

    const result = await platformCannon.fixFormatting({
      userId: user.uid,
      conversationId: currentConversationId,
      assistantMessageId: assistantMessageId,
    });

    if (result.success && result.contentForUI) {
      const finalUpdatePayload = {
        streamEvents: result.contentForUI,
        contentBlocks: [],
        toolCallsMap: {},
        status: 'delivered',
        'metadata.fixedFormatting': true,
      };

      for (const chunk of result.contentForUI) {
        finalUpdatePayload.contentBlocks = updateContentBlocksIncremental(
          finalUpdatePayload.contentBlocks,
          chunk,
          finalUpdatePayload.toolCallsMap
        );
      }
      finalUpdatePayload.contentBlocks = finalizeContentBlocks(finalUpdatePayload.contentBlocks);
      
      updateOptimisticAssistantMessage(currentConversationId, assistantMessageId, finalUpdatePayload);
      toast.success("Message formatting corrected.");
    } else {
      const errorMessage = result.error?.message || 'Failed to fix formatting.';
      handleError(errorMessage, result.error?.code || 'FIX_FORMATTING_FAILED');
      updateOptimisticAssistantMessage(currentConversationId, assistantMessageId, {
        status: 'delivered',
        errorDetails: { message: errorMessage, code: 'FIX_FORMAT_API_ERROR' },
      });
    }
  }, []);
  
  const redactMessageContent = useCallback(async (assistantMessageId, redactPayload) => {
    const { currentConversationId, updateOptimisticAssistantMessage, updateMessage, messages } = conversationHookDataRef.current;

    if (!currentConversationId || !assistantMessageId || !updateMessage) {
        handleError('Cannot redact content without required context or update capability.', 'REDACT_NO_CONTEXT');
        return;
    }

    const originalMessage = messages[currentConversationId]?.find(m => m.id === assistantMessageId);
    if (!originalMessage) {
        handleError('Original message not found for redaction.', 'REDACT_NO_MESSAGE');
        return;
    }

    try {
        const { blockId, codeToRedact } = redactPayload;
        if (!blockId || typeof codeToRedact !== 'string') {
            throw new Error('Invalid redaction payload. Requires blockId and codeToRedact.');
        }

        const replacementText = '[Code block removed by user]';

        const newContentBlocks = (originalMessage.contentBlocks || []).map(block => {
            if (block.id === blockId || (block.content === codeToRedact && block.type === 'code')) {
                return { type: 'text', content: `\`\`\`\n${replacementText}\n\`\`\``, id: block.id || blockId };
            }
            // This handles the new manual parsing where code blocks are inside text blocks
            if (block.type === 'text' && block.content?.includes(codeToRedact)) {
                return { ...block, content: block.content.replace(/```[\s\S]*?```/g, `\`\`\`\n${replacementText}\n\`\`\``) };
            }
            return block;
        });

        const newRawContent = (originalMessage.rawContent || '').replace(codeToRedact, replacementText);

        const optimisticUpdatePayload = {
            rawContent: newRawContent,
            contentBlocks: newContentBlocks,
            metadata: {
                ...(originalMessage.metadata || {}),
                redacted: true,
                redactedAt: new Date().toISOString(),
            },
            status: 'delivered',
        };

        // ARCHITECTURAL FIX: Update local state immediately and completely.
        // This stops the UI from reverting while waiting for the database listener.
        updateOptimisticAssistantMessage(currentConversationId, assistantMessageId, optimisticUpdatePayload);

        // Then, persist the exact same changes to the database.
        const result = await updateMessage(currentConversationId, assistantMessageId, {
            rawContent: newRawContent,
            contentBlocks: newContentBlocks,
            metadata: optimisticUpdatePayload.metadata
        });

        if (result.success) {
            toast.success("Content removed.");
        } else {
            throw new Error(result.error || 'Failed to save redaction to the database.');
        }
    } catch (error) {
        console.error("A critical error occurred during redaction:", error);
        toast.error(error.message || "An unexpected error occurred during redaction.");
        // If DB fails, revert the optimistic update to show the original content.
        updateOptimisticAssistantMessage(currentConversationId, assistantMessageId, {
             contentBlocks: originalMessage.contentBlocks,
             rawContent: originalMessage.rawContent,
             metadata: originalMessage.metadata,
             status: 'delivered' 
        });
    }
  }, []);


  const [useStreaming, setUseStreaming] = useState(true); 
  useEffect(() => { if (!settingsLoading) setUseStreaming(settingsUseStreaming); }, [settingsUseStreaming, settingsLoading]);

  const sendChatMessage = useCallback(async (messagePayloadFromInputArea, filesToUploadFromInputArea = [], otherOptionsFromInputArea = {}) => {
    const userMessageId = generateId('usr_msg');
    const assistantMessageId = generateId('asst_msg');
    const finalOptions = { ...otherOptionsFromInputArea, messageId: userMessageId, assistantMessageId, streamId: assistantMessageId };
    
    const userMessageTimestamp = await timestampService.getTimestamp();

    let coreMessageData;
    if (messagePayloadFromInputArea instanceof FormData) {
        const parsedPayload = JSON.parse(messagePayloadFromInputArea.get('json_payload'));
        const normalizedParsedMedia = Array.isArray(parsedPayload.currentMediaItems)
          ? parsedPayload.currentMediaItems
          : (Array.isArray(parsedPayload.mediaItems)
            ? parsedPayload.mediaItems
            : (Array.isArray(parsedPayload.images) ? parsedPayload.images : []));

        coreMessageData = { 
            rawContent: parsedPayload.rawContent || '', 
            contentBlocks: parsedPayload.contentBlocks || [],
            files: parsedPayload.files || {}, 
          currentMediaItems: normalizedParsedMedia,
            role: 'user', 
            timestamp: userMessageTimestamp, 
            hasAudio: parsedPayload.hasAudio || false,
        };
        const formDataFiles = messagePayloadFromInputArea.getAll('files');
        filesToUploadFromInputArea = formDataFiles.map(file => ({ blob: file, originalname: file.name }));
    } else {
        const validPayload = typeof messagePayloadFromInputArea === 'object' && messagePayloadFromInputArea !== null ? messagePayloadFromInputArea : { rawContent: '', files: {}, images: [] };
        const normalizedPayloadMedia = Array.isArray(validPayload.currentMediaItems)
          ? validPayload.currentMediaItems
          : (Array.isArray(validPayload.mediaItems)
            ? validPayload.mediaItems
            : (Array.isArray(validPayload.images) ? validPayload.images : []));
        coreMessageData = { 
            rawContent: validPayload.rawContent || '', 
            contentBlocks: validPayload.contentBlocks || [],
            files: validPayload.files || {}, 
          currentMediaItems: normalizedPayloadMedia,
            role: 'user', 
            timestamp: userMessageTimestamp, 
            hasAudio: validPayload.hasAudio || false,
        };
    }
    
    return useStreaming ? sendStreamingMessage(coreMessageData, finalOptions, filesToUploadFromInputArea) : sendMessage(coreMessageData, finalOptions, filesToUploadFromInputArea);
  }, [sendStreamingMessage, sendMessage, useStreaming]);
  
  const value = {
    sendMessage: sendChatMessage,
    cancelStream,
    useStreaming,
    setUseStreaming,
    fixMessageFormatting,
    redactMessageContent,
    localError,
  };

  return <ChatFeedContext.Provider value={value}>{children}</ChatFeedContext.Provider>;
};

export default ChatFeedManager;